import { useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import loadable from '@loadable/component';
import If from '@/components/If';
import { type NoteFragmentType } from '@/components/Note/hooks/useCompanyNotes';
import { CompanyNoteModalContext, ModalState } from './CompanyNoteModalContext';
import { getQueryKey as getNoteQueryKey } from '../../hooks/useNote';

const CompanyNoteModal = loadable(() => import('../CompanyNoteModal'));

interface Props {
  children: ReactNode;
}

const getNoteModalState = (noteId: string | undefined | null): ModalState => {
  if (noteId === null) {
    return 'idle';
  }
  if (typeof noteId === 'undefined') {
    return 'adding';
  }
  return 'editing';
};

export function CompanyNoteModalProvider({ children }: Props) {
  const { Provider } = CompanyNoteModalContext;
  const [noteId, setNoteId] = useState<string | undefined | null>(null);
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [uniqueSymbol, setUniqueSymbol] = useState<string | undefined>(
    undefined
  );
  const queryClient = useQueryClient();

  const openNoteModal = useCallback(
    ({
      companyId,
      uniqueSymbol,
      note,
    }: {
      companyId: string;
      uniqueSymbol: string;
      note?: NoteFragmentType;
    }) => {
      setNoteId(note?.id);
      setCompanyId(companyId);
      setUniqueSymbol(uniqueSymbol);
      if (note?.id) {
        queryClient.invalidateQueries({ queryKey: getNoteQueryKey(note.id) });
      }
    },
    []
  );

  const closeNoteModal = () => setNoteId(null);

  const modalState = getNoteModalState(noteId);

  return (
    <Provider
      value={{
        openNoteModal,
        closeNoteModal,
        modalState,
      }}
    >
      {children}
      <If match={modalState !== 'idle'}>
        {() => (
          <CompanyNoteModal
            noteId={noteId as string | undefined}
            companyId={companyId}
            uniqueSymbol={uniqueSymbol}
            onClose={closeNoteModal}
          />
        )}
      </If>
    </Provider>
  );
}
